/**
 * Keep text-node boundaries through react-snap's serialization.
 *
 * JSX like `risk ${price} to win` renders three adjacent text nodes. Serialized
 * to HTML they become one, so hydration expects "risk $" and finds the whole
 * sentence: React #425/#418, and the root is client-rendered from scratch (#423),
 * discarding the prerendered page. React's own server renderer avoids this by
 * writing `<!-- -->` between adjacent text nodes, which hydration skips. react-snap
 * serializes the live DOM instead, so nothing does it -- until this runs, from
 * window.snapSaveState, the moment before react-snap saves the page.
 *
 * Measured on /start: 33 such boundaries, and the prerendered page failed
 * hydration on every load. Routes are opted in one at a time, together with
 * utils/firstRender (see CLEAN_HYDRATION_ROUTES in src/index.js): on a page that
 * hydrates cleanly React keeps the prerendered DOM, so the snapshot must match
 * the page's first client render in attributes too.
 */
export const separateAdjacentText = (root) => {
  if (!root) return 0;
  const doc = root.ownerDocument;
  const walker = doc.createTreeWalker(root, 4 /* NodeFilter.SHOW_TEXT */);
  const needSeparator = [];
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    if (node.previousSibling && node.previousSibling.nodeType === 3) needSeparator.push(node);
  }
  needSeparator.forEach((node) => node.parentNode.insertBefore(doc.createComment(' '), node));
  return needSeparator.length;
};

/**
 * Text the minifier will rewrite. reactSnap.minifyHtml's collapseWhitespace (with
 * conservativeCollapse) turns every run of whitespace in a text node into one
 * space, so JSX that renders `{'• '}  ` or a newline inside text ships with
 * one space where React's first render has two, and hydration fails. A lone tab
 * is kept. <pre> and <textarea> keep their whitespace; script and style are not
 * page text.
 */
const COLLAPSIBLE = /[ \t\n\r\f]{2}|[\n\r\f]/;
const KEEPS_WHITESPACE = new Set(['PRE', 'TEXTAREA', 'SCRIPT', 'STYLE']);

export const collapsibleText = (root) => {
  if (!root) return [];
  const walker = root.ownerDocument.createTreeWalker(root, 4 /* NodeFilter.SHOW_TEXT */);
  const found = [];
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    if (!COLLAPSIBLE.test(node.data)) continue;
    let el = node.parentElement;
    while (el && el !== root && !KEEPS_WHITESPACE.has(el.tagName)) el = el.parentElement;
    if (!el || el === root) found.push(node.data);
  }
  return found;
};

export default separateAdjacentText;
